import { __decorate } from "tslib";
import { html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createRef } from 'lit/directives/ref.js';
import { listen, redispatchEvent, vdsEvent } from '../../base/events';
import { DEV_MODE } from '../../global/env';
import { CanPlay, MediaProviderElement, MediaType } from '../../media';
import { getSlottedChildren } from '../../utils/dom';
import { getNumberOfDecimalPlaces } from '../../utils/number';
import { keysOf } from '../../utils/object';
import { isNil, isNumber, isUndefined } from '../../utils/unit';
import { MediaNetworkState } from './MediaNetworkState';
import { MediaReadyState } from './MediaReadyState';
export const AUDIO_EXTENSIONS = /\.(m4a|mp4a|mpga|mp2|mp2a|mp3|m2a|m3a|wav|weba|aac|oga|spx)($|\?)/i;
export const VIDEO_EXTENSIONS = /\.(mp4|og[gv]|webm|mov|m4v)($|\?)/i;
/**
 * Enables loading, playing and controlling media files via the HTML5 MediaElement API. This is
 * used internally by the `vds-audio` and `vds-video` components. This provider only contains
 * glue code so don't bother using it on it's own.
 *
 * @slot Pass `<source>` and `<track>` elements to the underlying HTML5 media player.
 */
export class Html5MediaElement extends MediaProviderElement {
    constructor() {
        // -------------------------------------------------------------------------------------------
        // Properties
        // -------------------------------------------------------------------------------------------
        super(...arguments);
        /**
         * Determines what controls to show on the media element whenever the browser shows its own set
         * of controls (e.g. when the controls attribute is specified).
         *
         * @example 'nodownload nofullscreen noremoteplayback'
         * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/controlsList
         */
        this.controlsList = undefined;
        this._src = '';
        this._mediaRef = createRef();
        // -------------------------------------------------------------------------------------------
        // Time Updates
        // The `timeupdate` event fires surprisingly infrequently during playback, meaning your progress
        // bar (or whatever else is synced to the currentTime) moves in a choppy fashion. This helps
        // resolve that :)
        // -------------------------------------------------------------------------------------------
        this._timeRAF = undefined;
        this._isReplay = false;
        this._isLoopedReplay = false;
    }
    /**
     * The URL of a media resource to use.
     *
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/src
     */
    get src() {
        return this._src;
    }
    set src(newSrc) {
        if (this._src !== newSrc) {
            this._src = newSrc;
            this._handleMediaSrcChange();
        }
    }
    get mediaElement() {
        return this._mediaRef.value;
    }
    /**
     * Sets or returns the object which serves as the source of the media associated with the
     * `HTMLMediaElement`.
     *
     * @default undefined
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/srcObject
     */
    get srcObject() {
        var _a, _b;
        return (_b = (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.srcObject) !== null && _b !== void 0 ? _b : undefined;
    }
    set srcObject(newSrcObject) {
        if (!isUndefined(this.mediaElement) &&
            this.mediaElement.srcObject !== newSrcObject) {
            // TODO: this has to be attached after `firstUpdated`?
            this.mediaElement.srcObject = newSrcObject !== null && newSrcObject !== void 0 ? newSrcObject : null;
            this._handleMediaSrcChange();
        }
    }
    /**
     * Indicates the readiness state of the media.
     *
     * @default ReadyState.HaveNothing
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
     */
    get readyState() {
        var _a, _b;
        return (_b = (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.readyState) !== null && _b !== void 0 ? _b : MediaReadyState.HaveNothing;
    }
    /**
     * Indicates the current state of the fetching of media over the network.
     *
     * @default NetworkState.Empty
     */
    get networkState() {
        var _a, _b;
        return (_b = (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.networkState) !== null && _b !== void 0 ? _b : MediaNetworkState.Empty;
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    firstUpdated(changedProps) {
        super.firstUpdated(changedProps);
        this._bindMediaEventListeners();
    }
    disconnectedCallback() {
        this._isReplay = false;
        this._isLoopedReplay = false;
        this._replayTriggerEvent = undefined;
        super.disconnectedCallback();
        this._cancelTimeUpdates();
    }
    // -------------------------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------------------------
    /**
     * Override this to modify the content rendered inside `<audio>` and `<video>` elements.
     */
    _renderMediaChildren() {
        return html `
      <slot @slotchange="${this._handleDefaultSlotChange}"></slot>
      Your browser does not support the <code>audio</code> or
      <code>video</code> element.
    `;
    }
    _cancelTimeUpdates() {
        if (isNumber(this._timeRAF))
            window.cancelAnimationFrame(this._timeRAF);
        this._timeRAF = undefined;
    }
    _requestTimeUpdates() {
        // Time updates are already in progress.
        if (!isUndefined(this._timeRAF))
            return;
        this._requestTimeUpdate();
    }
    _requestTimeUpdate() {
        var _a, _b;
        const newTime = (_b = (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.currentTime) !== null && _b !== void 0 ? _b : 0;
        if (this.ctx.currentTime !== newTime) {
            this._updateCurrentTime(newTime);
        }
        this._timeRAF = window.requestAnimationFrame(() => {
            if (isUndefined(this._timeRAF))
                return;
            this._requestTimeUpdate();
        });
    }
    _updateCurrentTime(newTime, originalEvent) {
        // Avoid errors where `currentTime` can have higher precision than duration.
        this.ctx.currentTime = Math.min(newTime, this.duration);
        this.dispatchEvent(vdsEvent('vds-time-update', {
            detail: this.ctx.currentTime,
            originalEvent
        }));
    }
    // -------------------------------------------------------------------------------------------
    // Slots
    // -------------------------------------------------------------------------------------------
    _handleDefaultSlotChange() {
        if (isNil(this.mediaElement))
            return;
        this._cancelTimeUpdates();
        this._cleanupOldSourceNodes();
        this._attachNewSourceNodes();
    }
    _cleanupOldSourceNodes() {
        var _a;
        const nodes = (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.querySelectorAll('source,track');
        nodes === null || nodes === void 0 ? void 0 : nodes.forEach((node) => node.remove());
    }
    _attachNewSourceNodes() {
        const validTags = new Set(['source', 'track']);
        const nodes = getSlottedChildren(this).filter((node) => validTags.has(node.tagName.toLowerCase()));
        /* c8 ignore start */
        if (DEV_MODE && nodes.length > 0) {
            this._logger
                .logGroup('Found `<source>` and `<track>` elements')
                .appendWithLabel('Nodes', nodes)
                .end();
        }
        /* c8 ignore stop */
        nodes.forEach((node) => { var _a; return (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.appendChild(node.cloneNode()); });
        window.requestAnimationFrame(() => {
            this._handleMediaSrcChange();
        });
    }
    // -------------------------------------------------------------------------------------------
    // Events
    // -------------------------------------------------------------------------------------------
    _bindMediaEventListeners() {
        if (isNil(this.mediaElement))
            return;
        const eventListeners = {
            abort: this._handleAbort,
            canplay: this._handleCanPlay,
            canplaythrough: this._handleCanPlayThrough,
            durationchange: this._handleDurationChange,
            emptied: this._handleEmptied,
            ended: this._handleEnded,
            error: this._handleError,
            loadeddata: this._handleLoadedData,
            loadedmetadata: this._handleLoadedMetadata,
            loadstart: this._handleLoadStart,
            pause: this._handlePause,
            play: this._handlePlay,
            playing: this._handlePlaying,
            progress: this._handleProgress,
            ratechange: this._handleRateChange,
            seeked: this._handleSeeked,
            seeking: this._handleSeeking,
            stalled: this._handleStalled,
            suspend: this._handleSuspend,
            timeupdate: this._handleTimeUpdate,
            volumechange: this._handleVolumeChange,
            waiting: this._handleWaiting
        };
        keysOf(eventListeners).forEach((type) => {
            const handler = eventListeners[type].bind(this);
            this._disconnectDisposal.add(listen(this.mediaElement, type, async (event) => {
                /* c8 ignore start */
                if (DEV_MODE && type !== 'timeupdate') {
                    this._logger
                        .debugGroup(`📺 fired \`${event.type}\``)
                        .appendWithLabel('Event', event)
                        .appendWithLabel('Engine', this.engine)
                        .appendWithLabel('Context', this.mediaState)
                        .end();
                }
                /* c8 ignore stop */
                await handler(event);
                // re-dispatch native event for spec-compliance.
                redispatchEvent(this, event);
                this.requestUpdate();
            }));
        });
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger.debug('attached event listeners');
        }
        /* c8 ignore stop */
    }
    _handleAbort(event) {
        this.dispatchEvent(vdsEvent('vds-abort', { originalEvent: event }));
    }
    _handleCanPlay(event) {
        if (this.ctx.canPlay)
            return;
        this.ctx.buffered = this.mediaElement.buffered;
        this.ctx.seekable = this.mediaElement.seekable;
        if (!this._willAnotherEngineAttach())
            this._handleMediaReady(event);
    }
    _handleCanPlayThrough(event) {
        if (this.ctx.canPlayThrough)
            return;
        this.ctx.canPlayThrough = true;
        this.dispatchEvent(vdsEvent('vds-can-play-through', { originalEvent: event }));
    }
    _handleLoadStart(event) {
        this.ctx.currentSrc = this.mediaElement.currentSrc;
        this.dispatchEvent(vdsEvent('vds-load-start', { originalEvent: event }));
    }
    _handleEmptied(event) {
        this.dispatchEvent(vdsEvent('vds-emptied', { originalEvent: event }));
    }
    _handleLoadedData(event) {
        this.dispatchEvent(vdsEvent('vds-loaded-data', { originalEvent: event }));
    }
    /**
     * Can be used to indicate another engine such as `hls.js` will attach to the media element
     * so it can handle certain ready events.
     */
    _willAnotherEngineAttach() {
        return false;
    }
    _handleLoadedMetadata(event) {
        this.ctx.duration = this.mediaElement.duration;
        this.dispatchEvent(vdsEvent('vds-duration-change', {
            detail: this.ctx.duration,
            originalEvent: event
        }));
        this.dispatchEvent(vdsEvent('vds-loaded-metadata', { originalEvent: event }));
        this._determineMediaType(event);
    }
    _determineMediaType(event) {
        this.ctx.mediaType = this._getMediaType();
        this.dispatchEvent(vdsEvent('vds-media-type-change', {
            detail: this.ctx.mediaType,
            originalEvent: event
        }));
    }
    _handlePlay(event) {
        this.ctx.paused = false;
        if (this.ended || this._isReplay || this._isLoopedReplay) {
            this.ctx.ended = false;
            this._isReplay = false;
            const replayEvent = vdsEvent('vds-replay', { originalEvent: event });
            replayEvent.triggerEvent = this._replayTriggerEvent;
            this._replayTriggerEvent = undefined;
            this._playingTriggerEvent = replayEvent;
            this.dispatchEvent(replayEvent);
        }
        if (this._isLoopedReplay) {
            return;
        }
        const playEvent = vdsEvent('vds-play', { originalEvent: event });
        playEvent.autoplay = this._autoplayAttemptPending;
        this.dispatchEvent(playEvent);
        this._playingTriggerEvent = playEvent;
    }
    _handlePause(event) {
        // Don't fire if resuming from loop.
        if (this.loop &&
            // Avoid errors where `currentTime` can have higher precision than duration.
            Math.min(this.mediaElement.currentTime, this.duration) === this.duration) {
            return;
        }
        this._cancelTimeUpdates();
        this.ctx.paused = true;
        this.ctx.playing = false;
        this.ctx.waiting = false;
        this.dispatchEvent(vdsEvent('vds-pause', { originalEvent: event }));
    }
    _handlePlaying(event) {
        this.ctx.playing = true;
        this.ctx.waiting = false;
        this.ctx.ended = false;
        if (this._isLoopedReplay) {
            this._isLoopedReplay = false;
            this._requestTimeUpdates();
            return;
        }
        const playingEvent = vdsEvent('vds-playing', { originalEvent: event });
        playingEvent.triggerEvent = this._playingTriggerEvent;
        this._playingTriggerEvent = undefined;
        this.dispatchEvent(playingEvent);
        if (!this.ctx.started) {
            this.ctx.currentTime = 0;
            this.ctx.started = true;
            this.dispatchEvent(vdsEvent('vds-started', { originalEvent: event }));
        }
        this._requestTimeUpdates();
    }
    _handleDurationChange(event) {
        if (this.ended) {
            this._updateCurrentTime(this.mediaElement.duration, event);
        }
        this.ctx.duration = this.mediaElement.duration;
        this.dispatchEvent(vdsEvent('vds-duration-change', {
            detail: this.ctx.duration,
            originalEvent: event
        }));
    }
    _handleProgress(event) {
        this.ctx.buffered = this.mediaElement.buffered;
        this.ctx.seekable = this.mediaElement.seekable;
        this.dispatchEvent(vdsEvent('vds-progress', { originalEvent: event }));
    }
    _handleRateChange(event) {
        // TODO: no-op for now but we'll add playback rate support later.
        throw Error('Not implemented');
    }
    _handleSeeking(event) {
        this.ctx.currentTime = this.mediaElement.currentTime;
        this.ctx.seeking = true;
        if (this.ended) {
            this.ctx.ended = false;
            this._isReplay = true;
        }
        this.dispatchEvent(vdsEvent('vds-seeking', {
            detail: this.ctx.currentTime,
            originalEvent: event
        }));
    }
    _handleSeeked(event) {
        this.ctx.currentTime = this.mediaElement.currentTime;
        this.ctx.seeking = false;
        this.ctx.waiting = false;
        const seekedEvent = vdsEvent('vds-seeked', {
            detail: this.ctx.currentTime,
            originalEvent: event
        });
        this.dispatchEvent(seekedEvent);
        // Play or replay has greater priority.
        if (!this._playingTriggerEvent) {
            this._playingTriggerEvent = seekedEvent;
            setTimeout(() => {
                this._playingTriggerEvent = undefined;
            }, 150);
        }
        const currentTime = this.mediaElement.currentTime;
        // HLS: If precision has increased by seeking to the end, we'll call `play()` to properly end.
        if (Math.trunc(currentTime) === Math.trunc(this.duration) &&
            getNumberOfDecimalPlaces(this.duration) >
                getNumberOfDecimalPlaces(currentTime)) {
            this._updateCurrentTime(this.duration, event);
            if (!this.ended) {
                try {
                    this.play();
                }
                catch (e) {
                    // no-op
                }
            }
        }
    }
    _handleStalled(event) {
        this.dispatchEvent(vdsEvent('vds-stalled', { originalEvent: event }));
    }
    _handleTimeUpdate(event) {
        // -- Time updates are performed in `requestTimeUpdates()`.
    }
    _handleVolumeChange(event) {
        this.ctx.volume = this.mediaElement.volume;
        this.ctx.muted = this.mediaElement.muted;
        this.dispatchEvent(vdsEvent('vds-volume-change', {
            detail: {
                volume: this.ctx.volume,
                muted: this.ctx.muted
            },
            originalEvent: event
        }));
    }
    _handleWaiting(event) {
        this.ctx.playing = false;
        this.ctx.waiting = true;
        this.dispatchEvent(vdsEvent('vds-waiting', { originalEvent: event }));
    }
    _handleSuspend(event) {
        const suspendEvent = vdsEvent('vds-suspend', { originalEvent: event });
        this.dispatchEvent(suspendEvent);
    }
    _handleEnded(event) {
        this._cancelTimeUpdates();
        this.ctx.duration = this.mediaElement.duration;
        this._updateCurrentTime(this.ctx.duration, event);
        if (this.loop) {
            const loopedEvent = vdsEvent('vds-looped', { originalEvent: event });
            this._replayTriggerEvent = loopedEvent;
            this.dispatchEvent(loopedEvent);
            this._handleLoop();
        }
        else {
            this.ctx.ended = true;
            this.ctx.waiting = false;
            this.dispatchEvent(vdsEvent('vds-ended', { originalEvent: event }));
        }
    }
    _handleLoop() {
        window.requestAnimationFrame(async () => {
            try {
                this.mediaElement.controls = false;
                this._isLoopedReplay = true;
                await this.play();
                // We temporarily hide controls while looping to prevent flashing. Any of these events
                // will put the controls back in their previous state.
                const dispose = [];
                ['pointerdown', 'pointermove', 'keydown'].forEach((type) => {
                    dispose.push(listen(window, type, () => {
                        dispose.forEach((fn) => fn());
                        this.mediaElement.controls = this.controls;
                    }, { once: true }));
                });
            }
            catch (e) {
                this._isLoopedReplay = false;
                this.mediaElement.controls = this.controls;
            }
        });
    }
    _handleError(event) {
        this.ctx.error = this.mediaElement.error;
        this.dispatchEvent(vdsEvent('vds-error', {
            detail: this.mediaElement.error,
            originalEvent: event
        }));
    }
    // -------------------------------------------------------------------------------------------
    // Provider Methods
    // -------------------------------------------------------------------------------------------
    _getPaused() {
        return this.mediaElement.paused;
    }
    _getVolume() {
        return this.mediaElement.volume;
    }
    _setVolume(newVolume) {
        this.mediaElement.volume = newVolume;
    }
    _setCurrentTime(newTime) {
        if (this.mediaElement.currentTime !== newTime) {
            this.mediaElement.currentTime = newTime;
        }
    }
    _getMuted() {
        return this.mediaElement.muted;
    }
    _setMuted(isMuted) {
        this.mediaElement.muted = isMuted;
    }
    async _handleMediaSrcChange() {
        var _a;
        super._handleMediaSrcChange();
        if (!this._willAnotherEngineAttach()) {
            // Wait for `src` attribute to be updated on underlying `<audio>` or `<video>` element.
            await this.updateComplete;
            /* c8 ignore start */
            if (DEV_MODE) {
                this._logger.debug('Calling `load()` on media element');
            }
            /* c8 ignore stop */
            (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.load();
        }
    }
    // -------------------------------------------------------------------------------------------
    // Readonly Properties
    // -------------------------------------------------------------------------------------------
    get engine() {
        return this.mediaElement;
    }
    get buffered() {
        if (isNil(this.mediaElement))
            return new TimeRanges();
        return this.mediaElement.buffered;
    }
    /**
     * Returns a `MediaError` object for the most recent error, or `undefined` if there has not been
     * an error.
     *
     * @default undefined
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/error
     */
    get error() {
        var _a, _b;
        return (_b = (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.error) !== null && _b !== void 0 ? _b : undefined;
    }
    // -------------------------------------------------------------------------------------------
    // Methods
    // -------------------------------------------------------------------------------------------
    canPlayType(type) {
        if (isNil(this.mediaElement)) {
            return CanPlay.No;
        }
        return this.mediaElement.canPlayType(type);
    }
    async play() {
        var _a;
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger.info('attempting to play...');
        }
        /* c8 ignore stop */
        try {
            this._throwIfNotReadyForPlayback();
            await this._resetPlaybackIfEnded();
            return (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.play();
        }
        catch (error) {
            const playErrorEvent = vdsEvent('vds-play-error');
            playErrorEvent.autoplay = this._autoplayAttemptPending;
            playErrorEvent.error = error;
            throw error;
        }
    }
    async pause() {
        var _a;
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger.info('attempting to pause...');
        }
        /* c8 ignore stop */
        this._throwIfNotReadyForPlayback();
        return (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.pause();
    }
    /**
     * 🧑‍🔬 **EXPERIMENTAL:** Returns a `MediaStream` object which is streaming a real-time capture
     * of the content being rendered in the media element. This method will return `undefined`
     * if this API is not available.
     *
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/captureStream
     */
    captureStream() {
        var _a, _b;
        this._throwIfNotReadyForPlayback();
        return (_b = (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.captureStream) === null || _b === void 0 ? void 0 : _b.call(_a);
    }
    /**
     * Resets the media element to its initial state and begins the process of selecting a media
     * source and loading the media in preparation for playback to begin at the beginning. The
     * amount of media data that is prefetched is determined by the value of the element's
     * `preload` attribute.
     *
     * 💡 You should generally not need to call this method as it's handled by the library.
     *
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/load
     */
    load() {
        var _a;
        (_a = this.mediaElement) === null || _a === void 0 ? void 0 : _a.load();
    }
    _getMediaType() {
        if (AUDIO_EXTENSIONS.test(this.currentSrc)) {
            return MediaType.Audio;
        }
        if (VIDEO_EXTENSIONS.test(this.currentSrc)) {
            return MediaType.Video;
        }
        return MediaType.Unknown;
    }
}
__decorate([
    property()
], Html5MediaElement.prototype, "controlsList", void 0);
__decorate([
    property()
], Html5MediaElement.prototype, "crossOrigin", void 0);
__decorate([
    property({ type: Boolean })
], Html5MediaElement.prototype, "defaultMuted", void 0);
__decorate([
    property({ type: Number })
], Html5MediaElement.prototype, "defaultPlaybackRate", void 0);
__decorate([
    property({ type: Boolean })
], Html5MediaElement.prototype, "disableRemotePlayback", void 0);
__decorate([
    property()
], Html5MediaElement.prototype, "preload", void 0);
__decorate([
    property({ type: Number })
], Html5MediaElement.prototype, "width", void 0);
__decorate([
    property({ type: Number })
], Html5MediaElement.prototype, "height", void 0);
__decorate([
    state()
], Html5MediaElement.prototype, "_src", void 0);
__decorate([
    property()
], Html5MediaElement.prototype, "src", null);
